import * as XLSX from "xlsx";

const CUR_RX = /\b(TZS|USD|EUR|GBP|ZAR|KES|CNY|AED|INR)\b|\$|Currency|%|Qty|Rate|Days|No\b|Count/i;

// Currency labels: amount columns get the currency in the header; rows with mixed currencies get a Currency column.
function withCurrency(rows, columns) {
  const hasCurCol = columns.some((c) => c.key === "currency");
  const curs = new Set(rows.map((r) => r && r.currency).filter(Boolean));
  const isAmt = (c) => !CUR_RX.test(String(c.label)) && rows.some((r) => typeof (r || {})[c.key] === "number");
  let cols = columns.map((c) => (isAmt(c) && !hasCurCol && curs.size <= 1 ? { ...c, label: `${c.label} (${[...curs][0] || "TZS"})` } : c));
  if (!hasCurCol && curs.size > 1) cols = [...cols, { key: "currency", label: "Currency" }];
  return cols;
}

export function exportXlsx(filename, rows, columnsIn) {
  const columns = withCurrency(rows, columnsIn);
  const header = columns.map((c) => c.label);
  const numericKeys = {};
  const data = rows.map((row) =>
    columns.map((c) => {
      if (typeof row[c.key] === "number") numericKeys[c.key] = true;
      let v = c.format ? c.format(row[c.key], row) : row[c.key];
      return v === null || v === undefined ? "" : v;
    })
  );
  const aoa = [header, ...data];
  const hasNumeric = Object.keys(numericKeys).length > 0;
  if (hasNumeric && rows.length) {
    const totals = columns.map((c, i) => {
      if (numericKeys[c.key]) {
        const sum = rows.reduce((s, r) => s + (typeof r[c.key] === "number" ? r[c.key] : 0), 0);
        return c.format ? c.format(sum) : sum;
      }
      return i === 0 ? "TOTAL" : "";
    });
    aoa.push(totals);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = columns.map((c) => ({ wch: Math.max(12, String(c.label).length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}

// Backwards-compatible alias: existing call sites keep working, now producing .xlsx
export const exportToCsv = exportXlsx;

// Multi-sheet workbook export for board-pack style reports.
// Numeric values are written as real numbers (not pre-formatted strings) so they stay summable in Excel.
export function exportXlsxMulti(filename, sheets) {
  const wb = XLSX.utils.book_new();
  const used = {};
  (sheets || []).forEach(({ name, rows, columns, total }) => {
    const header = columns.map((c) => c.label);
    const numericKeys = {};
    const cell = (row, c) => {
      const raw = row[c.key];
      if (typeof raw === "number") { numericKeys[c.key] = true; return raw; }
      const v = c.format ? c.format(raw, row) : raw;
      return v === null || v === undefined ? "" : v;
    };
    const data = (rows || []).map((row) => columns.map((c) => cell(row, c)));
    const aoa = [header, ...data];
    if (total && rows && rows.length) {
      const totals = columns.map((c, i) => {
        if (numericKeys[c.key]) return rows.reduce((s, r) => s + (typeof r[c.key] === "number" ? r[c.key] : 0), 0);
        return i === 0 ? "TOTAL" : "";
      });
      aoa.push(totals);
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = columns.map((c) => ({ wch: Math.max(12, String(c.label).length + 2) }));
    let sn = (name || "Sheet").slice(0, 28);
    used[sn] = (used[sn] || 0) + 1;
    if (used[sn] > 1) sn = `${sn} ${used[sn]}`;
    XLSX.utils.book_append_sheet(wb, ws, sn);
  });
  XLSX.writeFile(wb, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
}
